import {
  useEffect,
  useState
} from 'react'

type VisualizationBlockKind =
  | 'metric_grid'
  | 'comparison'
  | 'bar_chart'
  | 'timeline'
  | 'process'
  | 'status_list'

type VisualizationTone = 'neutral' | 'positive' | 'attention'

interface VisualizationFact {
  label: string
  value: string
  emphasis: boolean
}

interface VisualizationItem {
  label: string
  value: string
  detail: string
  meta: string
  tone: VisualizationTone
  progress: number | null
  emphasis: boolean
  facts: VisualizationFact[]
}

interface VisualizationBlock {
  kind: VisualizationBlockKind
  title: string
  subtitle: string
  items: VisualizationItem[]
}

interface VisualizationSpec {
  eyebrow: string
  title: string
  subtitle: string
  takeaway: string
  blocks: VisualizationBlock[]
  source_note: string
}

declare global {
  interface Window {
    renderCatchVisualization?: (input: unknown) => void
  }
}

const BLOCK_KINDS = new Set<VisualizationBlockKind>([
  'metric_grid',
  'comparison',
  'bar_chart',
  'timeline',
  'process',
  'status_list'
])

const VISUALIZATION_TONES = new Set<VisualizationTone>([
  'neutral',
  'positive',
  'attention'
])

function isVisualizationFact(input: unknown): input is VisualizationFact {
  if (!input || typeof input !== 'object') {
    return false
  }
  const fact = input as Partial<VisualizationFact>
  return typeof fact.label === 'string'
    && typeof fact.value === 'string'
    && typeof fact.emphasis === 'boolean'
}

function isVisualizationItem(input: unknown): input is VisualizationItem {
  if (!input || typeof input !== 'object') {
    return false
  }
  const item = input as Partial<VisualizationItem>
  return typeof item.label === 'string'
    && typeof item.value === 'string'
    && typeof item.detail === 'string'
    && typeof item.meta === 'string'
    && VISUALIZATION_TONES.has(item.tone as VisualizationTone)
    && (item.progress === null || (
      typeof item.progress === 'number'
      && Number.isInteger(item.progress)
      && item.progress >= 0
      && item.progress <= 100
    ))
    && typeof item.emphasis === 'boolean'
    && Array.isArray(item.facts)
    && item.facts.every(isVisualizationFact)
}

function parseVisualizationSpec(input: unknown): VisualizationSpec {
  if (!input || typeof input !== 'object') {
    throw new Error('Visualization spec must be an object')
  }
  const candidate = input as Partial<VisualizationSpec>
  if (
    typeof candidate.title !== 'string'
    || typeof candidate.takeaway !== 'string'
    || !Array.isArray(candidate.blocks)
    || candidate.blocks.length < 1
    || candidate.blocks.some((block) => (
      !block
      || typeof block !== 'object'
      || !BLOCK_KINDS.has((block as VisualizationBlock).kind)
      || !Array.isArray((block as VisualizationBlock).items)
      || !(block as VisualizationBlock).items.every(isVisualizationItem)
    ))
  ) {
    throw new Error('Visualization spec is invalid')
  }
  return candidate as VisualizationSpec
}

function BlockHeading({
  title,
  subtitle
}: Pick<VisualizationBlock, 'title' | 'subtitle'>): JSX.Element | null {
  if (!title && !subtitle) {
    return null
  }
  return (
    <div className="mb-5">
      {title && (
        <h2 className="font-display text-[22px] font-medium leading-tight tracking-[-0.02em] text-catch-black">
          {title}
        </h2>
      )}
      {subtitle && (
        <p className="mt-1 text-[13px] leading-snug text-gray-500">
          {subtitle}
        </p>
      )}
    </div>
  )
}

function VisualizationBrand(): JSX.Element {
  return (
    <div className="flex items-center gap-2" aria-label="Catch">
      <svg
        viewBox="0 0 48 33"
        className="h-6 w-auto text-catch-red"
        fill="none"
        aria-hidden="true"
      >
        <path
          fill="currentColor"
          d="M47.22 10.04c-.71.99-1.67 1.76-3.6 1.76H30.68a11.7 11.7 0 0 0-2.87-4.74l17.35-.01c.56.01 1.06.15 1.52.39.96.52 1.21 1.68.54 2.6ZM25.64 19.91c1.46-1.99 1.06-7.11-4.29-3.15-6.58 4.87-9.73 5.1-10.37 3.27-.68-1.97.55-5.22 1.8-7.48 2.13-3.87 6.24-6.82 10.62-6.78h4.05c2.39 0 12.46-.02 14.81 0 2.35.01 3.51-.94 4.37-2.14.81-1.12.5-2.53-.66-3.15A3.83 3.83 0 0 0 44.14 0L18.2.01C9.12-.22 2.71 3.36.22 19.46c-1.41 9.12 4.09 17.93 15.38 9.69 8.01-5.85 8.44-7.07 10.04-9.24Zm18.44-6.43c-.45-.24-.95-.39-1.51-.39L31 13.1c.14.72.21 1.46.21 2.22 0 .86-.09 1.71-.27 2.52 3.25 0 8.58-.01 10.08 0 1.92 0 2.9-.78 3.61-1.77.66-.92.41-2.08-.55-2.59Zm-4.2 6.04c-.45-.25-.96-.39-1.51-.4h-7.79a11.72 11.72 0 0 1-3.09 4.76c3.24-.01 7.91 0 9.33-.01 1.94-.01 2.9-.77 3.61-1.76.66-.92.41-2.08-.55-2.59Z"
        />
      </svg>
      <span className="font-display text-[18px] font-medium uppercase leading-none tracking-[-0.04em] text-catch-black">
        Catch
      </span>
    </div>
  )
}

function ToneLabel({
  value,
  tone
}: {
  value: string
  tone: VisualizationTone
}): JSX.Element | null {
  if (!value) {
    return null
  }
  const className = tone === 'attention'
    ? 'bg-red-50 text-primary-700'
    : tone === 'positive'
      ? 'bg-catch-black text-catch-white'
      : 'bg-catch-grey text-gray-600'
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-[12px] font-medium leading-none ${className}`}>
      {value}
    </span>
  )
}

function MetricGrid({ block }: { block: VisualizationBlock }): JSX.Element {
  return (
    <section className="rounded-[22px] border border-catch-beige-dark bg-white p-6">
      <BlockHeading title={block.title} subtitle={block.subtitle} />
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl bg-catch-beige-dark">
        {block.items.map((item, index) => (
          <article
            key={`${item.label}-${index}`}
            className={`min-h-[126px] bg-catch-grey p-5 ${item.emphasis || item.tone === 'attention' ? 'border-t-[3px] border-catch-red' : ''}`}
          >
            <p className="text-[12px] font-medium uppercase tracking-[0.12em] text-gray-500">
              {item.label}
            </p>
            <p className="mt-2 font-display text-[36px] font-medium leading-none tracking-[-0.04em] text-catch-black tabular-nums">
              {item.value}
            </p>
            {item.detail && (
              <p className="mt-3 text-[13px] leading-snug text-gray-600">
                {item.detail}
              </p>
            )}
          </article>
        ))}
      </div>
    </section>
  )
}

function Comparison({
  block,
  wide
}: {
  block: VisualizationBlock
  wide: boolean
}): JSX.Element {
  const wideColumns = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4'
  }[block.items.length] ?? 'grid-cols-2'
  return (
    <section className="rounded-[22px] border border-catch-beige-dark bg-white p-6">
      <BlockHeading title={block.title} subtitle={block.subtitle} />
      <div className={wide ? `grid ${wideColumns} gap-3` : 'space-y-3'}>
        {block.items.map((item, index) => (
          <article
            key={`${item.label}-${index}`}
            className={`rounded-2xl border p-5 ${item.emphasis || item.tone === 'attention' ? 'border-catch-red bg-red-50/60' : item.tone === 'positive' ? 'border-catch-black bg-catch-grey' : 'border-catch-beige-dark bg-catch-grey'}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                {item.meta && (
                  <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.12em] text-gray-500">
                    {item.meta}
                  </p>
                )}
                <h3 className="font-display text-[20px] font-medium leading-tight text-catch-black">
                  {item.label}
                </h3>
              </div>
              {item.emphasis && (
                <span className="rounded-full bg-catch-red px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.08em] text-white">
                  Focus
                </span>
              )}
            </div>
            <dl className="mt-4 divide-y divide-catch-beige-dark">
              {item.facts.map((fact, factIndex) => (
                <div
                  key={`${fact.label}-${factIndex}`}
                  className="flex items-start justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
                >
                  <dt className="text-[12px] text-gray-500">
                    {fact.label}
                  </dt>
                  <dd className={`text-right text-[13px] font-medium leading-snug ${fact.emphasis ? 'text-catch-red' : 'text-catch-black'}`}>
                    {fact.value}
                  </dd>
                </div>
              ))}
            </dl>
            {item.detail && (
              <p className="mt-4 border-t border-catch-beige-dark pt-3 text-[12px] leading-snug text-gray-600">
                {item.detail}
              </p>
            )}
          </article>
        ))}
      </div>
    </section>
  )
}

function BarChart({ block }: { block: VisualizationBlock }): JSX.Element {
  return (
    <section className="rounded-[22px] border border-catch-beige-dark bg-white p-6">
      <BlockHeading title={block.title} subtitle={block.subtitle} />
      <div className="space-y-4">
        {block.items.map((item, index) => (
          <article key={`${item.label}-${index}`}>
            <div className="mb-2 flex items-end justify-between gap-4">
              <div className="min-w-0">
                <p className="truncate text-[13px] font-medium text-catch-black">
                  {item.label}
                </p>
                {item.detail && (
                  <p className="truncate text-[11px] text-gray-500">
                    {item.detail}
                  </p>
                )}
              </div>
              <p className="shrink-0 font-display text-[20px] font-medium leading-none text-catch-black tabular-nums">
                {item.value}
              </p>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-catch-grey-dark" aria-hidden="true">
              <div
                className={item.emphasis ? 'h-full rounded-full bg-catch-red' : 'h-full rounded-full bg-catch-black'}
                style={{ width: `${item.progress ?? 0}%` }}
              />
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

function Timeline({ block }: { block: VisualizationBlock }): JSX.Element {
  return (
    <section className="rounded-[22px] border border-catch-beige-dark bg-white p-6">
      <BlockHeading title={block.title} subtitle={block.subtitle} />
      <ol className="relative ml-2 border-l border-catch-beige-dark">
        {block.items.map((item, index) => (
          <li key={`${item.label}-${index}`} className="relative pb-5 pl-7 last:pb-0">
            <span className={`absolute -left-[6px] top-1 h-[11px] w-[11px] rounded-full ring-4 ring-white ${item.emphasis ? 'bg-catch-red' : 'bg-catch-black'}`} />
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="text-[14px] font-medium leading-tight text-catch-black">
                {item.label}
              </h3>
              {item.meta && (
                <span className="shrink-0 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500">
                  {item.meta}
                </span>
              )}
            </div>
            {item.detail && (
              <p className="mt-1 text-[12px] leading-snug text-gray-600">
                {item.detail}
              </p>
            )}
          </li>
        ))}
      </ol>
    </section>
  )
}

function Process({
  block,
  wide
}: {
  block: VisualizationBlock
  wide: boolean
}): JSX.Element {
  return (
    <section className="rounded-[22px] border border-catch-beige-dark bg-white p-6">
      <BlockHeading title={block.title} subtitle={block.subtitle} />
      <ol className={wide ? 'grid grid-flow-col auto-cols-fr gap-3' : 'space-y-3'}>
        {block.items.map((item, index) => (
          <li
            key={`${item.label}-${index}`}
            className={`relative rounded-2xl p-4 ${item.emphasis ? 'bg-catch-black text-white' : 'bg-catch-grey text-catch-black'}`}
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <span className={`flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-medium ${item.emphasis ? 'bg-catch-red text-white' : 'bg-white text-catch-black'}`}>
                {index + 1}
              </span>
              {item.meta && (
                <span className={`text-[10px] font-medium uppercase tracking-[0.1em] ${item.emphasis ? 'text-white/60' : 'text-gray-500'}`}>
                  {item.meta}
                </span>
              )}
            </div>
            <h3 className="text-[14px] font-medium leading-tight">
              {item.label}
            </h3>
            {item.detail && (
              <p className={`mt-2 text-[12px] leading-snug ${item.emphasis ? 'text-white/70' : 'text-gray-600'}`}>
                {item.detail}
              </p>
            )}
          </li>
        ))}
      </ol>
    </section>
  )
}

function StatusList({ block }: { block: VisualizationBlock }): JSX.Element {
  return (
    <section className="rounded-[22px] border border-catch-beige-dark bg-white p-6">
      <BlockHeading title={block.title} subtitle={block.subtitle} />
      <div className="divide-y divide-catch-beige-dark">
        {block.items.map((item, index) => (
          <article key={`${item.label}-${index}`} className="py-3.5 first:pt-0 last:pb-0">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="truncate text-[14px] font-medium text-catch-black">
                    {item.label}
                  </h3>
                  {item.meta && (
                    <span className="shrink-0 text-[11px] text-gray-500">
                      {item.meta}
                    </span>
                  )}
                </div>
                {item.detail && (
                  <p className="mt-1 truncate text-[12px] text-gray-600">
                    {item.detail}
                  </p>
                )}
              </div>
              <ToneLabel value={item.value} tone={item.tone} />
            </div>
            {item.progress !== null && (
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-catch-grey-dark" aria-hidden="true">
                <div
                  className={item.tone === 'attention' ? 'h-full rounded-full bg-catch-red' : 'h-full rounded-full bg-catch-black'}
                  style={{ width: `${item.progress}%` }}
                />
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  )
}

function VisualizationBlockView({
  block,
  wide
}: {
  block: VisualizationBlock
  wide: boolean
}): JSX.Element {
  switch (block.kind) {
    case 'metric_grid':
      return <MetricGrid block={block} />
    case 'comparison':
      return <Comparison block={block} wide={wide} />
    case 'bar_chart':
      return <BarChart block={block} />
    case 'timeline':
      return <Timeline block={block} />
    case 'process':
      return <Process block={block} wide={wide} />
    case 'status_list':
      return <StatusList block={block} />
  }
}

function VisualizationCanvas({ spec }: { spec: VisualizationSpec }): JSX.Element {
  const oneBlock = spec.blocks.length === 1
  return (
    <main
      data-visualization-ready="true"
      className="flex h-[900px] w-[1200px] flex-col overflow-hidden bg-catch-white px-12 py-10 text-catch-black"
    >
      <header className="flex items-center justify-between border-b border-catch-beige-dark pb-5">
        <VisualizationBrand />
        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-gray-500">
          Prepared by Catch
        </p>
      </header>

      <section className="grid grid-cols-[minmax(0,1fr)_360px] items-end gap-10 py-7">
        <div>
          <p className="mb-3 text-[12px] font-medium uppercase tracking-[0.16em] text-catch-red">
            {spec.eyebrow}
          </p>
          <h1 className="max-w-[720px] font-display text-[48px] font-medium leading-[0.98] tracking-[-0.045em] text-catch-black">
            {spec.title}
          </h1>
          {spec.subtitle && (
            <p className="mt-4 max-w-[720px] text-[16px] leading-snug text-gray-600">
              {spec.subtitle}
            </p>
          )}
        </div>
        <aside className="rounded-[18px] border-l-4 border-catch-red bg-red-50 px-5 py-4">
          <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-primary-700">
            The takeaway
          </p>
          <p className="text-[17px] font-medium leading-snug tracking-[-0.01em] text-catch-black">
            {spec.takeaway}
          </p>
        </aside>
      </section>

      <div className={`min-h-0 flex-1 items-start ${oneBlock ? '' : spec.blocks.length === 2 ? 'grid grid-cols-2 gap-5' : 'grid grid-cols-3 gap-4'}`}>
        {spec.blocks.map((block, index) => (
          <VisualizationBlockView
            key={`${block.kind}-${index}`}
            block={block}
            wide={oneBlock}
          />
        ))}
      </div>

      <footer className="mt-4 flex min-h-4 items-end justify-between gap-6">
        <p className="truncate text-[10px] text-gray-500">
          {spec.source_note}
        </p>
        <p className="shrink-0 text-[10px] font-medium uppercase tracking-[0.12em] text-gray-500">
          Executive visual
        </p>
      </footer>
    </main>
  )
}

export function ExecutiveVisualizationPage(): JSX.Element {
  const [spec, setSpec] = useState<VisualizationSpec | null>(null)

  useEffect(() => {
    window.renderCatchVisualization = (input: unknown): void => {
      setSpec(parseVisualizationSpec(input))
    }
    return () => {
      delete window.renderCatchVisualization
    }
  }, [])

  if (!spec) {
    return (
      <main className="flex h-[900px] w-[1200px] items-center justify-center bg-catch-white">
        <p className="font-display text-xl text-gray-500">
          Waiting for visualization spec…
        </p>
      </main>
    )
  }
  return <VisualizationCanvas spec={spec} />
}
